import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { accessLevelsApi } from '../../../administration/access-levels/accessLevelsApi';
import type { AccessLevel } from '../../../administration/access-levels/accessLevelTypes';
import { casesApi } from '../casesApi';
import type { CaseListItem } from '../caseTypes';

const NONE = 'none';

/**
 * Classifies a case. The personnel record's own form, one module along — the same ladder, the
 * same consequence stated before the tick.
 *
 * No Autogenerate: the only field is a choice whose consequence the form states, and a generated
 * rung could take the case out of its own editor's reach — the "never a field the reader has
 * already decided" rule, reached from the other side.
 */
export function CaseAccessLevelModal({
  record,
  onClose,
  onSaved,
}: {
  record: CaseListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [levelId, setLevelId] = useState<string>(record.accessLevelId ?? NONE);
  const [levels, setLevels] = useState<AccessLevel[]>([]);
  const [loadingLevels, setLoadingLevels] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    accessLevelsApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setLevels(next);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoadingLevels(false);
      });

    return () => controller.abort();
  }, []);

  const chosen = levelId === NONE ? null : levelId;
  const chosenLevel = levels.find((l) => l.id === chosen) ?? null;
  const canSave = confirmed && !saving && !loadingLevels;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await casesApi.setAccessLevel(record.id, chosen);
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not set the access level.', 0);

      // A 403 here is the server saying the new level is above the reader's own clearance —
      // the save happened, and the case is now out of reach. Reported as what it is.
      if (failure.isForbidden) {
        onSaved();
        return;
      }

      setError(failure);
      focusValidationError(failure, { idPrefix: 'case-access-level' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Set access level"
      size="md"
      id="case-access-level-modal"
      confirm={{ id: 'case-access-level-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="case-access-level-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="case-access-level-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Select
          id="case-access-level-access-level-id"
          label="Access level"
          description="The clearance a member needs to open this case and its evidence. A stronger rung is read by fewer people."
          value={levelId}
          onChange={(next) => setLevelId(next || NONE)}
          loading={loadingLevels}
          disabled={saving}
          sort="none"
          clearable={false}
          options={[
            { value: NONE, label: 'Unclassified', sublabel: 'Readable by every member of the case' },
            ...levels.map((level) => ({
              value: level.id,
              label: level.name,
              sublabel: level.description ?? undefined,
              right: <span className="font-mono tabular-nums">Rank {level.rank}</span>,
            })),
          ]}
          error={error?.fieldError('accessLevelId')}
        />

        <p className="text-xs text-slate-500">
          {chosenLevel
            ? `Only members tagged ${chosenLevel.name} or stronger will be able to open this case. Other members still see it listed, with its title masked.`
            : 'Every member of the case will be able to open it.'}{' '}
          If your own clearance does not reach the level you choose, you will lose access to this
          case when you submit.
        </p>
      </div>
    </Modal>
  );
}
